import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FenceService } from '../../../../../../services/configurators-2d/applications/fence.service';
import { BoardStateService } from '../../../services/board.state.service';
import { Board } from '../../../../../../models/configurators-2d/applications/fence/board';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { logError } from '../../../../../../services/helpers/sentry.helper';
import {
  ConfigurationInstallation
} from '../../../../../../models/configurators-2d/applications/fence/configuration-installation';
import { MaterialsFacadeService } from '../../services/materials.facade.service';
import { ColorStateService } from '../../../services/color.state.service';
import { Color } from '../../../../../../models/configurators-2d/applications/fence/color';
import { ConfigurationBoard } from '../../../../../../models/configurators-2d/applications/fence/configuration-board';
import { Installation } from '../../../../../../models/configurators-2d/applications/fence/installations/installation';
import { InstallationStateService } from '../../../services/installation.state.service';
import { getMaxHeight } from '../../../../../../services/helpers/fence-helper';
import { Configuration } from '../../../../../../models/configurators-2d/applications/fence/configuration';
import { MaxBoardService } from '../../services/max-board.service';
import { ModalService } from '../../../../../../services/modal.service';

@Component({
  selector: 'ng-casto-materials-installation-boards',
  templateUrl: './materials-installation-boards.component.html',
  styleUrl: './materials-installation-boards.component.scss',
})
export class MaterialsInstallationBoardsComponent implements OnInit {
  @Input() configurationInstallation: ConfigurationInstallation;

  private readonly fenceService = inject(FenceService);
  private readonly boardStateService = inject(BoardStateService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly materialsFacadeService = inject(MaterialsFacadeService);
  private readonly colorStateService = inject(ColorStateService);
  private readonly installationStateService = inject(InstallationStateService);
  private readonly maxBoardService = inject(MaxBoardService);
  private readonly modalService = inject(ModalService);

  /** Base URL de l'API */
  baseImgURL: string = undefined;

  /** Langue sélectionnée */
  currentLang: string;

  protected get boards(): Board[] {
    return this.boardStateService.getBoardByInstallation(this.configurationInstallation.installationId);
  }

  protected get colorsActiveCount(): number {
    return this.colorStateService.colorsActiveCountByInstallations().get(this.configurationInstallation.installationId);
  }

  /**
   * Retourne la liste des couleurs disponibles pour le type de lame sélectionné
   */
  protected get colors(): Color[] {
    return this.colorStateService.colorsByInstallations().get(this.configurationInstallation.installationId);
  }

  /**
   * Est-ce que la configurationInstallation a des types de lame ?
   */
  protected get configurationInstallationHasBoards(): boolean {
    return this.configurationInstallation.configurationBoards.length > 0;
  }

  /**
   * Est-ce que la configurationInstallation contient au moins une lame mixte ?
   */
  protected get configurationInstallationContainBoardMix(): boolean {
    return this.configurationInstallation.configurationBoards.length > 1;
  }

  /**
   * Récupère la couleur secondaire d'une lame mixte
   */
  protected get boardMixSecondaryBoardQuantity(): number {
    return this.boardMixSecondaryBoard?.boardNumber ?? 0;
  }

  /**
   * Vérifie dans quel sens est affiché le mixage des lames
   * @protected
   */
  protected get isBoardMixAscOrder(): boolean {
    const primaryBoardOrder = this.boardMixPrimaryBoard?.order ?? 0;
    const secondaryBoardOrder = this.boardMixSecondaryBoard?.order ?? 0;
    return primaryBoardOrder < secondaryBoardOrder;
  }

  get boardMixSecondaryBoard(): ConfigurationBoard {
    return this.configurationInstallation.configurationBoards.find(b => b.boardNumber);
  }

  private get boardMixPrimaryBoard(): ConfigurationBoard {
    return this.configurationInstallation.configurationBoards.find(b => !b.boardNumber);
  }

  protected get installation(): Installation {
    return this.installationStateService.getById(this.configurationInstallation.installationId);
  }

  private get configuration(): Configuration {
    return this.materialsFacadeService.configuration();
  }

  protected get maxBoard(): number {
    return this.maxBoardService.maxBoardFromSecondaryInstallationSecondaryBoard(
      this.configurationInstallation,
      this.configuration.configurationInstallations,
      getMaxHeight(this.configuration)
    );
  }

  private getBoardMixSecondaryBoard(): ConfigurationBoard {
    return this.configurationInstallation.configurationBoards.find(b => b.boardNumber);
  }

  /**
   * Récupère le nom de lame secondaire
   * @returns nom de la lame secondaire
   */
  get secondaryBoardName(): string {
    const secondaryBoard = this.getBoardMixSecondaryBoard();
    return (this.currentLang === 'fr' ? secondaryBoard?.boardNameFr : secondaryBoard?.boardNameEn) ?? '';
  }

  constructor() {
    this.baseImgURL = this.fenceService.apiUrl;
  }

  ngOnInit(): void {
    this.currentLang = this.translateService.getBrowserLang();
  }

  /**
   * Sélectionne une lame et met à jour la configuration
   * @param board - La lame sélectionnée
   */
  protected selectBoard(board: Board): void {
    this.materialsFacadeService
      .selectBoard(this.configurationInstallation, board)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur sélection board:', err),
      });
  }

  /**
   * Vérifie si une lame est actuellement sélectionnée dans la configuration
   * @param board - Lame
   * @returns true si la lame est sélectionnée, false sinon
   */
  protected isBoardSelected(board: Board): boolean {
    return this.configurationInstallation.configurationBoards.some(cb => cb.fenceTypeId === board.id);
  }

  /**
   * Sélectionne une couleur et met à jour la configuration
   * @param color - La couleur sélectionnée
   */
  protected selectColor(color: Color): void {
    // pas de sélection si la lame est désactivée
    if (color.isDisabled) {
      return;
    }

    this.materialsFacadeService
      .selectColor(this.configurationInstallation, color)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur sélection color:', err),
      });
  }

  /**
   * Vérifie si une couleur est actuellement sélectionnée dans la configuration
   * @param color - Couleur
   * @returns true si la couleur est sélectionnée, false sinon
   */
  protected isColorSelected(color: Color): boolean {
    return this.materialsFacadeService.isColorSelected(this.configurationInstallation, color);
  }

  /**
   * Met à jour la prévisualisation après modification du nombre de lames mixtes
   */
  protected changeNbLamesMixte(changeEvent: Event): void {
    const domInput = changeEvent.target as HTMLInputElement;
    const newQuantity = Number.parseInt(domInput.value);

    if (Number.isNaN(newQuantity) || newQuantity <= 0 || newQuantity > this.maxBoard) {
      domInput.value = this.boardMixSecondaryBoardQuantity.toString();
      return;
    }

    this.materialsFacadeService
      .changeNbLamesMixte(this.configurationInstallation, newQuantity)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur changement nombre lames mixtes:', err),
      });
  }

  /**
   * Met à jour la prévisualisation après modification de l'ordre des lames mixtes
   */
  protected changeBoardMixOrder(): void {
    this.materialsFacadeService
      .changeBoardMixOrder(this.configurationInstallation)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: err => logError('Erreur changement ordre lames:', err),
      });
  }

  protected openTooltip(board: Board, $event: MouseEvent): void {
    this.modalService.displayInfo(board.tooltip, board.nameFr, board.nameEn);
    $event.stopPropagation();
  }
}
